import { motion } from 'framer-motion';
import { GlassCard } from '../components/ui/GlassCard';
import { Server, Database, Globe, Shield, Cpu, Share2, Layers, Binary } from 'lucide-react';

const systems = [
    {
        name: "Hybrid VM Gate",
        description: "Standardized VPS/VM entry point with Traefik auto-discovery.",
        icon: <Globe />,
        layers: ["Edge Routing", "SSL Termination", "Load Balancing"]
    },
    {
        name: "Neural Sync v2",
        description: "Vector document vault with real-time semantic search indexing.",
        icon: <Cpu />,
        layers: ["Vector Store", "Similarity Search", "RAG Pipeline"]
    },
    {
        name: "Sentinel Mesh",
        description: "Distributed health monitoring with concurrent node validation.",
        icon: <Shield />,
        layers: ["Heartbeat API", "Alert Gateway", "Self-Healing"]
    },
    {
        name: "Data Flow Core",
        description: "Hierarchical scraping matrix with distributed task distribution.",
        icon: <Share2 />,
        layers: ["Queue Master", "Worker Nodes", "Result aggregator"]
    }
];

export function Architecture() {
    return (
        <div className="pt-32 pb-48 px-6 md:px-12 max-w-7xl mx-auto min-h-screen">
            <div className="fixed inset-0 z-0 opacity-20 pointer-events-none">
                <img
                    src="/futuristic-distributed-intelligence-architecture-blueprint.png"
                    alt="Architecture Blueprint"
                    className="w-full h-full object-cover blur-3xl scale-110"
                />
            </div>

            <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                className="mb-32 max-w-3xl relative z-10"
            >
                <div className="flex items-center gap-4 mb-6">
                    <Binary className="text-emerald-500 w-6 h-6" />
                    <span className="text-xs font-mono text-emerald-500 uppercase tracking-[0.4em]">Structural Patterns</span>
                </div>
                <h1 className="text-6xl md:text-8xl font-black text-white tracking-tighter mb-8">
                    SYSTEM <br />
                    <span className="text-emerald-500">SCHEMATICS</span>
                </h1>
                <p className="text-gray-400 font-mono text-sm leading-loose uppercase tracking-widest">
                    A deep dive into the underlying architectural frameworks that power the Lehana.in ecosystem.
                </p>
            </motion.div>

            {/* Interactive Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
                {systems.map((system, idx) => (
                    <motion.div
                        key={system.name}
                        initial={{ opacity: 0, scale: 0.95 }}
                        whileInView={{ opacity: 1, scale: 1 }}
                        viewport={{ once: true }}
                        transition={{ delay: idx * 0.1 }}
                    >
                        <GlassCard className="p-10 border-white/5 hover:border-emerald-500/30 transition-all duration-700 bg-black/60 relative overflow-hidden group">
                            <div className="relative z-10">
                                <div className="flex items-start justify-between mb-12">
                                    <div className="p-5 bg-emerald-500/10 rounded-2xl text-emerald-500 border border-emerald-500/20 group-hover:bg-emerald-500 group-hover:text-black transition-all duration-500">
                                        {system.icon}
                                    </div>
                                    <Layers className="text-gray-800 w-12 h-12 opacity-30" />
                                </div>

                                <h3 className="text-2xl font-bold text-white mb-4 uppercase tracking-tight">{system.name}</h3>
                                <p className="text-gray-400 text-sm font-mono mb-12 leading-relaxed h-12">
                                    {system.description}
                                </p>

                                {/* Visual Layers Representation */}
                                <div className="space-y-3">
                                    {system.layers.map((layer, lIdx) => (
                                        <motion.div
                                            key={layer}
                                            initial={{ x: -20, opacity: 0 }}
                                            whileInView={{ x: 0, opacity: 1 }}
                                            transition={{ delay: (idx * 0.1) + (lIdx * 0.1) }}
                                            className="flex items-center gap-4 p-3 bg-white/5 border border-white/5 rounded-lg group-hover:border-emerald-500/20 transition-all"
                                        >
                                            <span className="text-[10px] font-mono text-emerald-500/50">L_0{lIdx + 1}</span>
                                            <span className="text-xs font-mono text-gray-300 uppercase tracking-widest">{layer}</span>
                                        </motion.div>
                                    ))}
                                </div>
                            </div>

                            {/* Decorative Background Symbol */}
                            <div className="absolute -bottom-10 -right-10 opacity-5 group-hover:opacity-10 transition-opacity pointer-events-none">
                                {system.icon}
                            </div>
                        </GlassCard>
                    </motion.div>
                ))}
            </div>

            {/* Core Stack Visualization */}
            <section className="mt-48 py-24 border-y border-white/5">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-16">
                    <div className="space-y-6">
                        <Server className="text-emerald-500 w-10 h-10" />
                        <h4 className="text-xl font-bold text-white uppercase italic">Infrastructure</h4>
                        <p className="text-gray-500 text-xs font-mono tracking-widest leading-loose">
                            DOCKERIZED_ENVIRONMENT<br />
                            NGINX_PROXY_MANAGER<br />
                            AWS_LAMBDA_MICROSERVICES
                        </p>
                    </div>
                    <div className="space-y-6">
                        <Database className="text-emerald-500 w-10 h-10" />
                        <h4 className="text-xl font-bold text-white uppercase italic">Data Persistance</h4>
                        <p className="text-gray-500 text-xs font-mono tracking-widest leading-loose">
                            ELASTICSEARCH_INDEXING<br />
                            POSTGRESQL_RELATIONAL_DATA<br />
                            REDIS_MEMORY_CACHING
                        </p>
                    </div>
                    <div className="space-y-6">
                        <Shield className="text-emerald-500 w-10 h-10" />
                        <h4 className="text-xl font-bold text-white uppercase italic">Security Layer</h4>
                        <p className="text-gray-500 text-xs font-mono tracking-widest leading-loose">
                            SSL_TSL_TERMINATION<br />
                            JWT_AUTH_PROTOCOL<br />
                            COLD_STORAGE_ENCRYPTION
                        </p>
                    </div>
                </div>
            </section>

            <div className="mt-48 text-center text-[10px] font-mono text-gray-700 tracking-[1.5em] uppercase">
                System Schema: v4.11_ALPHA // Protocol Ref: lehana_mcp_std
            </div>
        </div>
    );
}
